/**
 * PURPOSE: Estado de la vista previa de la planilla FEDUP: pide el PDF, lo expone como blob URL y lo libera.
 * RESPONSIBILITIES:
 *  - Sí: cada apertura vuelve a pedir el PDF al backend (siempre datos frescos) y `reload` lo regenera sin cerrar.
 *  - Sí: cancelar la petición en curso al cerrar o al refrescar; descartar respuestas viejas.
 *  - Sí: liberar el blob URL al cerrar, al refrescar y al desmontar.
 *  - No: no toca el estado de la planilla ni descarga nada; los errores llegan clasificados para mostrarlos en el visor.
 * COLLABORATORS:
 *  - FedupPreviewModal.jsx, fedupPreview.utils.js, servicios de PDF por deporte (loadPdf)
 * ROUTE: src/components/FullScreenModal/FullScreenPlanilla/shared/useFedupPreview.js
 */

import { useCallback, useEffect, useRef, useState } from "react";
import { classifyFedupPreviewError, ensurePdfBlob } from "./fedupPreview.utils.js";

const IDLE = { status: "idle", url: "", error: null, updatedAt: null };

export function useFedupPreview({ open, matchId, loadPdf }) {
  const [state, setState] = useState(IDLE);
  const controllerRef = useRef(null);
  const requestIdRef = useRef(0);
  const urlRef = useRef("");
  const loadPdfRef = useRef(loadPdf);
  loadPdfRef.current = loadPdf;

  const releaseUrl = useCallback(() => {
    if (urlRef.current) {
      window.URL.revokeObjectURL(urlRef.current);
      urlRef.current = "";
    }
  }, []);

  const load = useCallback(async () => {
    controllerRef.current?.abort();
    const controller = new AbortController();
    controllerRef.current = controller;
    const requestId = ++requestIdRef.current;
    const isStale = () => controller.signal.aborted || requestId !== requestIdRef.current;

    // Al refrescar se conserva el PDF anterior a la vista hasta que llegue el nuevo.
    setState((prev) => ({ ...prev, status: "loading", error: null }));

    try {
      if (!matchId) {
        const err = new Error("matchId requerido para la vista previa.");
        err.status = 400;
        throw err;
      }
      const blob = ensurePdfBlob(await loadPdfRef.current(matchId, { signal: controller.signal }));
      if (isStale()) return;

      const previousUrl = urlRef.current;
      const nextUrl = window.URL.createObjectURL(blob);
      urlRef.current = nextUrl;
      setState({ status: "ready", url: nextUrl, error: null, updatedAt: new Date() });
      if (previousUrl) window.URL.revokeObjectURL(previousUrl);
    } catch (err) {
      if (isStale()) return;
      const info = await classifyFedupPreviewError(err);
      if (info.kind === "canceled" || isStale()) return;
      releaseUrl();
      setState({ status: "error", url: "", error: info, updatedAt: null });
    }
  }, [matchId, releaseUrl]);

  useEffect(() => {
    if (!open) return undefined;
    load();
    return () => {
      controllerRef.current?.abort();
      controllerRef.current = null;
      requestIdRef.current += 1;
      releaseUrl();
      setState(IDLE);
    };
  }, [open, load, releaseUrl]);

  return { ...state, reload: load };
}
